import { useState, useCallback } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';
import { Order } from '../interface/interface';

interface FetchOrdersParams {
  page: number;
  size: number;
}

interface FeedbackMessage {
  id: string;
  message: string;
  sender: {
    name: string;
  };
  createdAt: string;
}

interface SendFeedbackParams {
  orderId: string;
  message: string;
  fromBuyer?: boolean;
}

interface OrdersResponse {
  content: Order[];
  totalElements: number;
}

export const useOrder = () => {
  const { enhancedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // Fetch orders with pagination
  const fetchOrders = useCallback(
    async (params: FetchOrdersParams): Promise<OrdersResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const response = await enhancedFetch(
          `${API.HOST}/api/v1/orders?page=${params.page}&size=${params.size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        return response;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch orders');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [enhancedFetch]
  );

  // Fetch single order details
  const fetchOrderDetails = useCallback(
    async (orderId: string): Promise<Order | null> => {
      setLoading(true);
      setError(null);

      try {
        const response = await enhancedFetch(
          `${API.HOST}/api/v1/orders/${orderId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        return response;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch order details');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [enhancedFetch]
  );

  // Fetch order status options
  const fetchOrderStatuses = useCallback(async (): Promise<string[] | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await enhancedFetch(
        `${API.HOST}/api/v1/order-statuses`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return response;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch order statuses');
      return null;
    } finally {
      setLoading(false);
    }
  }, [enhancedFetch]);

  // Update order status
  const updateOrderStatus = useCallback(
    async (orderId: string, newStatus: string): Promise<boolean> => {
      setStatusUpdateLoading(true);
      setError(null);

      try {
        await enhancedFetch(`${API.HOST}/api/v1/orders/status/${orderId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        });

        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to update order status');
        return false;
      } finally {
        setStatusUpdateLoading(false);
      }
    },
    [enhancedFetch]
  );

  // Send feedback message
  const sendFeedbackMessage = useCallback(
    async (params: SendFeedbackParams): Promise<boolean> => {
      setFeedbackLoading(true);
      setError(null);

      try {
        await enhancedFetch(
          `${API.HOST}/api/v1/orders/${params.orderId}/feedback`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              orderId: params.orderId,
              message: params.message.trim(),
              fromBuyer: params.fromBuyer || false
            })
          }
        );

        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to send feedback message');
        return false;
      } finally {
        setFeedbackLoading(false);
      }
    },
    [enhancedFetch]
  );

  // Fetch feedback messages for an order
  const fetchFeedbackMessages = useCallback(
    async (orderId: string): Promise<FeedbackMessage[] | Response | null> => {
      setFeedbackLoading(true);
      setError(null);

      try {
        return await enhancedFetch(
          `${API.HOST}/api/v1/orders/${orderId}/feedback`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
      } catch (err: any) {
        setError(err.message || 'Failed to fetch feedback messages');
        return null;
      } finally {
        setFeedbackLoading(false);
      }
    },
    [enhancedFetch]
  );

  const deleteFeedbackMessage = useCallback(
    async (orderId: string, messageId: string): Promise<boolean> => {
      setFeedbackLoading(true);
      setError(null);
      try {
        await enhancedFetch(
          `${API.HOST}/api/v1/orders/${orderId}/feedback/${messageId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to delete feedback message');
        return false;
      } finally {
        setFeedbackLoading(false);
      }
    },
    [enhancedFetch]
  );

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Functions
    fetchOrders,
    fetchOrderDetails,
    fetchOrderStatuses,
    updateOrderStatus,
    sendFeedbackMessage,
    fetchFeedbackMessages,
    deleteFeedbackMessage,
    clearError,

    // State
    loading,
    feedbackLoading,
    statusUpdateLoading,
    error
  };
};
